import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SCOPE_LABELS } from '../../../core/occurrence/occurrence.labels';
import { DatePicker } from '../../../shared/date-picker/date-picker';
import { PageHeader } from '../../../shared/page-header/page-header';
import { ConfirmDialog } from '../../../shared/confirm-dialog/confirm-dialog';
import { AuthService } from '../../../core/auth/auth.service';
import { UserService } from '../../../core/user/user.service';
import {
  InviteUserResult, ROLE_LABELS, ROLE_OPTIONS, STATUS_LABELS, User, UserModule, UserRole,
} from '../../../core/user/user.models';

@Component({
  selector: 'app-user-list',
  imports: [ReactiveFormsModule, FormsModule, DatePicker, PageHeader, ConfirmDialog],
  templateUrl: './user-list.html',
})
export class UserList implements OnInit {
  private readonly service = inject(UserService);
  private readonly auth = inject(AuthService);
  private readonly fb = inject(FormBuilder);

  protected readonly users = signal<User[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly actionError = signal<string | null>(null);

  protected readonly showInvite = signal(false);
  protected readonly inviteError = signal<string | null>(null);
  protected readonly inviteResult = signal<InviteUserResult | null>(null);
  protected readonly linkCopied = signal(false);

  // Módulos: a matriz abre na linha do utilizador, sem sair da lista.
  protected readonly openModulesFor = signal<User | null>(null);
  protected readonly modules = signal<UserModule[]>([]);
  protected readonly modulesLoading = signal(false);
  protected readonly modulesSaving = signal(false);
  protected readonly modulesError = signal<string | null>(null);
  protected readonly scopeLabels = SCOPE_LABELS;

  protected readonly roleLabels = ROLE_LABELS;
  protected readonly roleOptions = ROLE_OPTIONS;
  protected readonly statusLabels = STATUS_LABELS;

  protected readonly currentEmail = computed(() => this.auth.tenantSession()?.email ?? '');

  protected readonly inviteForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    role: ['AGENT' as UserRole, Validators.required],
  });

  ngOnInit(): void {
    this.load();
  }

  protected openModules(user: User): void {
    if (this.openModulesFor()?.id === user.id) {
      this.closeModules();
      return;
    }
    this.openModulesFor.set(user);
    this.modulesError.set(null);
    this.modulesLoading.set(true);
    this.service.modules(user.id).subscribe({
      next: (list) => {
        this.modules.set(list.map((module) => ({ ...module })));
        this.modulesLoading.set(false);
      },
      error: () => {
        this.modulesError.set('Não foi possível carregar os módulos.');
        this.modulesLoading.set(false);
      },
    });
  }

  protected closeModules(): void {
    this.openModulesFor.set(null);
    this.modules.set([]);
  }

  /** Quem edita ou extrai também vê: marcar um implica o "ver". */
  protected toggleModule(module: UserModule,
      field: 'canView' | 'canEdit' | 'canCreate' | 'canExport', value: boolean): void {
    module[field] = value;
    if (value && field !== 'canView') module.canView = true;
    if (!value && field === 'canView') {
      module.canEdit = false;
      module.canCreate = false;
      module.canExport = false;
    }
    this.modules.set([...this.modules()]);
  }

  protected saveModules(): void {
    const user = this.openModulesFor();
    if (!user) return;
    this.modulesError.set(null);
    this.modulesSaving.set(true);
    this.service.setModules(user.id, this.modules()).subscribe({
      next: () => {
        this.modulesSaving.set(false);
        this.closeModules();
      },
      error: (error) => {
        this.modulesSaving.set(false);
        this.modulesError.set(error?.error?.message ?? 'Não foi possível guardar os módulos.');
      },
    });
  }

  private load(): void {
    this.loading.set(true);
    this.service.list().subscribe({
      next: (list) => {
        this.users.set(list);
        this.loading.set(false);
      },
      error: (response) => {
        this.error.set(response?.status === 403
          ? 'Apenas administradores podem gerir utilizadores.'
          : 'Não foi possível carregar os utilizadores.');
        this.loading.set(false);
      },
    });
  }

  protected openInvite(): void {
    this.inviteResult.set(null);
    this.inviteError.set(null);
    this.inviteForm.reset({ name: '', email: '', role: 'AGENT' });
    this.showInvite.set(true);
  }

  protected submitInvite(): void {
    if (this.inviteForm.invalid) {
      this.inviteForm.markAllAsTouched();
      return;
    }
    this.inviteError.set(null);
    this.service.invite(this.inviteForm.getRawValue()).subscribe({
      next: (result) => {
        this.users.update((list) => [result.user, ...list]);
        this.inviteResult.set(result);
        this.linkCopied.set(false);
        this.showInvite.set(false);
      },
      error: (response) => this.inviteError.set(response?.error?.message ?? 'Não foi possível convidar o utilizador.'),
    });
  }

  protected changeRole(user: User, event: Event): void {
    this.actionError.set(null);
    const role = (event.target as HTMLSelectElement).value as UserRole;
    this.service.updateRole(user.id, role).subscribe({
      next: (updated) => this.replace(updated),
      error: (response) => {
        this.actionError.set(response?.error?.message ?? 'Não foi possível alterar o papel.');
        this.load(); // reverte a seleção
      },
    });
  }

  protected toggleStatus(user: User): void {
    this.actionError.set(null);
    this.service.setStatus(user.id, user.status !== 'ACTIVE').subscribe({
      next: (updated) => this.replace(updated),
      error: (response) => this.actionError.set(response?.error?.message ?? 'Não foi possível alterar o status.'),
    });
  }

  /** Reposição do acesso pedida — à espera de confirmação (deita abaixo a senha de alguém). */
  protected readonly pendingReset = signal<User | null>(null);

  protected askReset(user: User): void {
    this.actionError.set(null);
    this.pendingReset.set(user);
  }

  protected cancelReset(): void {
    this.pendingReset.set(null);
  }

  protected confirmReset(): void {
    const user = this.pendingReset();
    this.pendingReset.set(null);
    if (user) this.resend(user);
  }

  protected resend(user: User): void {
    this.actionError.set(null);
    this.service.resendInvite(user.id).subscribe({
      next: (result) => {
        this.inviteResult.set(result); // mostra a caixa com o novo link
        this.linkCopied.set(false);
        // Repor o acesso muda o estado da pessoa: sem isto a linha continuava a dizer "Ativo"
        // até alguém recarregar a página.
        this.replace(result.user);
      },
      error: (response) => this.actionError.set(response?.error?.message ?? 'Não foi possível reenviar o convite.'),
    });
  }

  protected copyLink(url: string): void {
    navigator.clipboard?.writeText(url).then(() => this.linkCopied.set(true));
  }

  protected isSelf(user: User): boolean {
    return user.email.toLowerCase() === this.currentEmail().toLowerCase();
  }

  protected initial(name: string): string {
    return (name?.trim()?.[0] ?? '?').toUpperCase();
  }

  protected statusTone(status: string): string {
    return status === 'ACTIVE' ? 'success' : status === 'INVITED' ? 'warning' : 'secondary';
  }

  private replace(updated: User): void {
    this.users.update((list) => list.map((user) => (user.id === updated.id ? updated : user)));
  }
}
